import { Injectable, inject } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { HttpClient } from '@angular/common/http';
import { from, Observable, of } from 'rxjs';
import { catchError, map, tap, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AiTranslationService {
  private supabase = inject(SupabaseService);
  private http = inject(HttpClient);

  // In-memory cache for current session
  private memoryCache: Record<string, string> = {};

  translate(text: string, targetLang: string): Observable<string> {
    if (!text || !targetLang || targetLang === 'es') {
      return of(text);
    }

    const cacheKey = `${text}_${targetLang}`;
    if (this.memoryCache[cacheKey]) {
      return of(this.memoryCache[cacheKey]);
    }

    // 1. Check Supabase Cache
    return from(
      this.supabase.publicClient
        .from('ai_translations')
        .select('translated_text')
        .eq('original_text', text)
        .eq('target_lang', targetLang)
        .maybeSingle()
    ).pipe(
      switchMap(({ data, error }) => {
        if (data && data.translated_text) {
          this.memoryCache[cacheKey] = data.translated_text;
          return of(data.translated_text);
        }

        // 2. Call AI (via Supabase Edge Function)
        return this.callAiTranslation(text, targetLang).pipe(
          tap(translated => {
            if (translated && translated !== text) {
              this.memoryCache[cacheKey] = translated;
              this.saveToCache(text, targetLang, translated);
            }
          })
        );
      }),
      catchError(err => {
        console.error('Translation error:', err);
        return of(text); // Fallback to original text
      })
    );
  }

  private callAiTranslation(text: string, targetLang: string): Observable<string> {
    // Proposing a Supabase Edge Function 'translate-ai'
    // This is more secure than calling OpenAI directly from frontend
    const url = `${environment.supabaseUrl}/functions/v1/translate-ai`;
    const headers = {
      'Authorization': `Bearer ${environment.supabaseKey}`,
      'Content-Type': 'application/json'
    };

    return this.http.post<{ translatedText: string }>(url, { text, targetLang }, { headers }).pipe(
      map(res => res.translatedText || text),
      catchError(() => of(text))
    );
  }

  private async saveToCache(original_text: string, target_lang: string, translated_text: string) {
    try {
      await this.supabase.client
        .from('ai_translations')
        .upsert({ original_text, target_lang, translated_text }, { onConflict: 'original_text,target_lang' });
    } catch (e) {
      // Silent error for cache saving
    }
  }
}
